import React, { useRef, useEffect, useCallback } from "react";
import MenuList from "./MenuList";
import { Mode } from "../../views/tutorV2/types";
import { useStorageMenuListView } from "../../views/tutorV2/hooks/presentation/useStorageMenuListView";
import { useUIStore, useTutorChatStore } from "../../views/tutorV2/store";
import { useMediaMd } from "../../hooks/useMedia";

/**
 * StorageMenuList
 *
 * 보관함 목록(튜터 보관함/소크라테스 보관함) 컨테이너 컴포넌트
 *
 * 책임:
 * - useStorageMenuListView 호출하여 데이터 및 핸들러 가져오기
 * - Intersection Observer로 무한 스크롤 구현
 * - MenuList 프레젠테이션 컴포넌트에 props 전달
 *
 * 참고:
 * - 보관함은 편집/저장 불가 (삭제만 가능)
 */

export interface StorageMenuListProps {
  mode: Mode;
}

const StorageMenuList: React.FC<StorageMenuListProps> = ({ mode }) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  const {
    items,
    label,
    isLoading,
    hasMore,
    isFetchingMore,
    handleItemClick,
    handleItemDelete,
    handleLoadMore,
  } = useStorageMenuListView({ mode });
  const { openStorage, setOpenStorage, closeNav } = useUIStore();
  const { fetching } = useTutorChatStore();
  const isMd = useMediaMd();

  // Intersection Observer로 무한 스크롤 구현
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isFetchingMore) {
        handleLoadMore();
      }
    },
    [hasMore, isFetchingMore, handleLoadMore]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, { threshold: 0 });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  return (
    <MenuList
      label={label}
      icon="document"
      items={items}
      open={openStorage}
      setOpen={setOpenStorage}
      isLoading={isLoading}
      onItemClick={(itemId) => {
        handleItemClick(itemId);
        if (!isMd) closeNav();
      }}
      onItemDelete={handleItemDelete}
      // 보관함은 편집/저장 불가
      onItemEdit={undefined}
      onItemSave={undefined}
      onItemTitleChange={undefined}
      editingItemId={null}
      observerTarget={observerTarget}
      hasMore={hasMore}
      disabled={fetching}
    />
  );
};

export default StorageMenuList;
